<script setup lang="ts">
  import { computed } from 'vue';

  import type { Contact } from '@stuut/api/models';
  import { EXTERNAL_ROLE_LABELS, INTERNAL_ROLE_LABELS } from '@stuut/api/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';
  import { CopyButton } from '@ui/components/button';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  interface Props {
    contact: Contact;
  }

  const props = defineProps<Props>();

  const role = computed(() => {
    if (props.contact?.is_primary_email) {
      return 'Primary Billing Contact';
    }
    return props.contact?.external_role
      ? EXTERNAL_ROLE_LABELS[props.contact?.external_role]
      : props.contact?.internal_role
        ? INTERNAL_ROLE_LABELS[props.contact?.internal_role]
        : undefined;
  });
</script>

<template>
  <div class="flex flex-col gap-4 w-full text-xs">
    <div class="grid grid-cols-2 items-center justify-end gap-x-4 truncate">
      <span class="text-muted-foreground">Name</span>

      <span v-if="contact?.name" class="truncate text-right">{{ contact?.name }}</span>
      <span v-else class="text-muted-foreground text-right">Not set</span>
    </div>

    <div class="grid grid-cols-2 items-center justify-end gap-x-4 truncate">
      <span class="text-muted-foreground">Email Address</span>

      <Tooltip v-if="contact?.email">
        <CopyButton
          :copy-value="contact.email"
          class="px-0 justify-end"
          variant="transparent"
          size="sm"
        >
          <div class="flex items-center justify-end gap-1.5 truncate">
            <TooltipTrigger as-child>
              <span
                class="truncate"
                :class="{ 'line-through text-muted-foreground': contact?.is_email_disabled }"
              >
                {{ contact?.email }}
              </span>
            </TooltipTrigger>

            <i class="far fa-copy text-xs text-muted-foreground"></i>
          </div>
        </CopyButton>

        <TooltipContent side="right">
          {{ contact?.is_email_disabled ? 'Email is disabled' : contact?.email }}
        </TooltipContent>
      </Tooltip>

      <span v-else class="text-muted-foreground text-right">Not set</span>
    </div>

    <div class="grid grid-cols-2 items-center justify-end gap-x-4 truncate">
      <span class="text-muted-foreground">Phone</span>

      <Tooltip v-if="contact?.phone">
        <CopyButton
          :copy-value="contact.phone"
          class="px-0 justify-end"
          variant="transparent"
          size="sm"
        >
          <div class="flex items-center justify-end gap-1.5 truncate">
            <TooltipTrigger as-child>
              <span
                class="truncate"
                :class="{ 'line-through text-muted-foreground': contact?.is_phone_disabled }"
              >
                {{ formatPhoneNumber(contact?.phone) }}
              </span>
            </TooltipTrigger>

            <i class="far fa-copy text-xs text-muted-foreground"></i>
          </div>
        </CopyButton>

        <TooltipContent side="right">
          {{
            contact?.is_phone_disabled
              ? 'Phone number is disabled'
              : formatPhoneNumber(contact?.phone)
          }}
        </TooltipContent>
      </Tooltip>

      <span v-else class="text-muted-foreground text-right">Not set</span>
    </div>

    <div class="grid grid-cols-2 items-center justify-end gap-x-4 truncate">
      <span class="text-muted-foreground">Job Title</span>

      <span v-if="contact?.job_title" class="truncate text-right">{{ contact?.job_title }}</span>
      <span v-else class="text-muted-foreground text-right">Not set</span>
    </div>

    <div class="grid grid-cols-2 items-center justify-end gap-x-4 truncate">
      <span class="text-muted-foreground">Role</span>

      <span v-if="role" class="truncate text-right">{{ role }}</span>
      <span v-else class="text-muted-foreground text-right">Not set</span>
    </div>

    <div
      v-for="(field, key) in contact.custom_fields"
      :key="key"
      class="grid grid-cols-2 items-center justify-end gap-x-4 truncate"
    >
      <span class="text-muted-foreground">{{ field.label }}</span>
      <span class="truncate text-right">{{ field.value }}</span>
    </div>
  </div>
</template>

<style scoped></style>
